import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const previousHashRef = useRef(hash);
  const previousPathnameRef = useRef(pathname);

  // Function to calculate header height dynamically
  const getHeaderHeight = () => {
    // Try to find header elements
    const header = document.querySelector('header');
    const nav = document.querySelector('nav');
    
    if (header) {
      return header.offsetHeight;
    }
    
    // Fallback: check for common header classes
    const headerByClass = document.querySelector('.header, [class*="Header"]');
    if (headerByClass) {
      return headerByClass.offsetHeight;
    }
    
    // Default fallback height
    return 100;
  };

  // Function to scroll to hash element with proper offset
  const scrollToHash = (targetHash = hash) => {
    if (!targetHash) return;
    
    try {
      const element = document.querySelector(targetHash);
      if (element) {
        // Get header height dynamically
        const headerHeight = getHeaderHeight();
        
        // Get element position
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        
        // Calculate scroll position with offset
        const scrollPosition = elementPosition - headerHeight - 20; // 20px extra padding
        
        // Scroll to position
        window.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
        
        return true;
      }
    } catch (error) {
      console.warn('Error scrolling to hash:', error);
    }
    
    return false;
  };

  // Handle hash-based scrolling
  useEffect(() => {
    if (hash) {
      // If hash changed, scroll to it
      if (previousHashRef.current !== hash) {
        // Multiple attempts to handle dynamic content loading
        const attempts = [100, 300, 600, 1000];
        const timeouts = attempts.map(delay => 
          setTimeout(() => {
            if (scrollToHash(hash)) {
              // Successfully scrolled, can clear remaining timeouts
              attempts.forEach((_, index) => {
                if (index > attempts.indexOf(delay)) {
                  clearTimeout(timeouts[index]);
                }
              });
            }
          }, delay)
        );

        previousHashRef.current = hash;

        return () => {
          timeouts.forEach(timeout => clearTimeout(timeout));
        };
      } else {
        // Hash is the same but might be a re-click, still scroll
        const timeoutId = setTimeout(() => scrollToHash(hash), 100);
        return () => clearTimeout(timeoutId);
      }
    } else {
      // Scroll to top when there's no hash (only on pathname change)
      if (previousPathnameRef.current !== pathname) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        previousPathnameRef.current = pathname;
      }
      previousHashRef.current = null;
    }
  }, [pathname, hash]);

  // Handle browser back/forward navigation with hash
  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash;
      if (currentHash) {
        // Use multiple attempts for browser navigation
        setTimeout(() => scrollToHash(currentHash), 100);
        setTimeout(() => scrollToHash(currentHash), 500);
      }
    };

    const handlePopState = () => {
      // Handle browser back/forward
      setTimeout(() => {
        const currentHash = window.location.hash;
        if (currentHash) {
          scrollToHash(currentHash);
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Scroll to top on initial mount if no hash
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  return null;
};

export default ScrollToTop;